import { apiClient } from "../client";

export interface User {
  id: number;
  email: string;
  googleId: string | null;
  name: string;
  profilePicture: string | null;
  isVerified: boolean;
  verificationCode: string;
  verificationCodeExpiry: string;
  onboardingCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Organization {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  url: string | null;
  createdBy: number;
  country: string | null;
  state: string | null;
  city: string | null;
  street: string | null;
  postalCode: string | null;
  domain: string;
  currency: string;
  currencySymbol: string;
  timezone: string;
  timezoneOffset: string;
  dateFormat: string;
  financialYearStart: string;
  createdAt: string;
  updatedAt: string;
}

export interface Member {
  id: number;
  userId: number | null;
  siteId: number;
  user: User | null;
  email: string;
  invitationToken: string;
  invitationTokenExpiry: string;
  role: string;
  status: string;
  dateJoined: string | null;
  invitedBy: number;
  invitationAcceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Site {
  id: number;
  name: string;
  iconColor: string;
  systemCode: string;
  country: string;
  state: string;
  city: string;
  street: string;
  postalCode: string;
  organizationId: number;
  organization?: Organization;
  members: Member[];
  userRole?: Member;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  role?: string;
  fields?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GetSitesResponse {
  sites: Site[];
  pagination?: PaginationMeta;
}

export interface SiteMembersResponse {
  message: string;
  site: {
    id: number;
    name: string;
    systemCode: string;
  };
  members: Array<{
    id: number;
    email: string;
    name: string;
    status: string;
    role: string;
    dateJoined: string | null;
    createdAt: string;
    profilePicture: string | null;
    userId: number | null;
  }>;
  pagination: PaginationMeta;
}

export interface InviteMemberResponse {
  message: string;
  invitations: Array<{
    id: number;
    email: string;
    role: string;
    status: string;
    invitationToken: string;
  }>;
  skipped?: Array<{
    email: string;
    reason: string;
  }>;
}

export interface CreateSiteData {
  name: string;
  iconColor?: string;
  systemCode?: string;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  postalCode?: string;
}

export interface CreateSiteResponse {
  message: string;
  site: Site;
}

export interface UpdateSiteData {
  name?: string;
  iconColor?: string;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  postalCode?: string;
}

export interface UpdateSiteResponse {
  message: string;
  site: Site;
}

export interface DeleteSiteResponse {
  message: string;
}

export interface RemoveMemberResponse {
  message: string;
}

export interface ResendInviteResponse {
  message: string;
  invitationToken: string;
}

export interface UpdateMemberRoleResponse {
  message: string;
  member: Member;
}

export interface UpdateMemberStatusResponse {
  message: string;
  member: Member;
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface AxiosErrorResponse {
  response?: {
    data: ApiError;
  };
  message: string;
}

export interface UserRoleData {
  role: string;
  isOrganizationOwner: boolean;
  siteId: number;
}

export class SiteService {
  static async getSites(
    organizationId: number,
    params?: PaginationParams,
  ): Promise<GetSitesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.fields) queryParams.append("fields", params.fields);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites?${queryString}`
      : `/organizations/${organizationId}/sites`;

    return await apiClient.get<GetSitesResponse>(url);
  }

  static async getSiteById(
    organizationId: number,
    siteId: number,
  ): Promise<Site> {
    return await apiClient.get<Site>(
      `/organizations/${organizationId}/sites/${siteId}`,
    );
  }

  static async createSite(
    organizationId: number,
    siteData: CreateSiteData,
  ): Promise<CreateSiteResponse> {
    return await apiClient.post<CreateSiteResponse>(
      `/organizations/${organizationId}/sites`,
      siteData,
    );
  }

  static async updateSite(
    organizationId: number,
    siteId: number,
    siteData: UpdateSiteData,
  ): Promise<UpdateSiteResponse> {
    return await apiClient.put<UpdateSiteResponse>(
      `/organizations/${organizationId}/sites/${siteId}`,
      siteData,
    );
  }

  static async deleteSite(
    organizationId: number,
    siteId: number,
  ): Promise<DeleteSiteResponse> {
    return await apiClient.delete<DeleteSiteResponse>(
      `/organizations/${organizationId}/sites/${siteId}`,
    );
  }



  static async getUserRoleInSite(
    organizationId: number,
    siteId: number,
  ): Promise<{
    message: string;
    role: UserRoleData;
  }> {
    return await apiClient.get<{
      message: string;
      role: UserRoleData;
    }>(`/organizations/${organizationId}/sites/${siteId}/my-role`);
  }

  static async getSiteMembers(
    organizationId: number,
    siteId: number,
    params?: PaginationParams,
  ): Promise<SiteMembersResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.role) queryParams.append("role", params.role);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/members?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/members`;

    return await apiClient.get<SiteMembersResponse>(url);
  }

  static async inviteSiteMembers(
    organizationId: number,
    siteId: number,
    emails: string[],
    role: string,
  ): Promise<InviteMemberResponse> {
    return await apiClient.post<InviteMemberResponse>(
      `/organizations/${organizationId}/sites/${siteId}/invite`,
      { emails, role },
    );
  }

  static async resendMemberInvitation(
    organizationId: number,
    siteId: number,
    memberId: number,
  ): Promise<ResendInviteResponse> {
    return await apiClient.post<ResendInviteResponse>(
      `/organizations/${organizationId}/sites/${siteId}/members/${memberId}/resend-invite`,
      {},
    );
  }

  static async removeSiteMember(
    organizationId: number,
    siteId: number,
    memberId: number,
  ): Promise<RemoveMemberResponse> {
    return await apiClient.delete<RemoveMemberResponse>(
      `/organizations/${organizationId}/sites/${siteId}/members/${memberId}`,
    );
  }

  static async updateMemberRole(
    organizationId: number,
    siteId: number,
    memberId: number,
    role: string,
  ): Promise<UpdateMemberRoleResponse> {
    return await apiClient.put<UpdateMemberRoleResponse>(
      `/organizations/${organizationId}/sites/${siteId}/members/${memberId}/role`,
      { role },
    );
  }

  static async updateMemberStatus(
    organizationId: number,
    siteId: number,
    memberId: number,
    status: string,
  ): Promise<UpdateMemberStatusResponse> {
    return await apiClient.put<UpdateMemberStatusResponse>(
      `/organizations/${organizationId}/sites/${siteId}/members/${memberId}/status`,
      { status },
    );
  }
}
